import axios from 'axios';
import {
    LOCATION_LIST_REQUEST, LOCATION_LIST_SUCCESS, LOCATION_LIST_FAIL,
    LOCATION_LIST_BY_IDS_REQUEST, LOCATION_LIST_BY_IDS_SUCCESS, LOCATION_LIST_BY_IDS_FAIL,
    LOCATION_DETAILS_REQUEST, LOCATION_DETAILS_SUCCESS, LOCATION_DETAILS_FAIL,
    LOCATION_CREATE_REQUEST, LOCATION_CREATE_SUCCESS, LOCATION_CREATE_FAIL,
    LOCATION_UPDATE_REQUEST, LOCATION_UPDATE_SUCCESS, LOCATION_UPDATE_FAIL,
    LOCATION_DELETE_REQUEST, LOCATION_DELETE_SUCCESS, LOCATION_DELETE_FAIL,
    LOCATION_DTO_REQUEST, LOCATION_DTO_SUCCESS, LOCATION_DTO_FAIL,
    LOCATION_BY_ORGANISATION_REQUEST, LOCATION_BY_ORGANISATION_SUCCESS, LOCATION_BY_ORGANISATION_FAIL,
    LOCATION_BY_STATUS_REQUEST, LOCATION_BY_STATUS_SUCCESS, LOCATION_BY_STATUS_FAIL,
    LOCATION_BY_ODS_CODE_REQUEST, LOCATION_BY_ODS_CODE_SUCCESS, LOCATION_BY_ODS_CODE_FAIL,
    LOCATION_BY_LOCATION_CODE_REQUEST, LOCATION_BY_LOCATION_CODE_SUCCESS, LOCATION_BY_LOCATION_CODE_FAIL,
    LOCATION_BY_UPLOAD_BATCH_REQUEST, LOCATION_BY_UPLOAD_BATCH_SUCCESS, LOCATION_BY_UPLOAD_BATCH_FAIL,
    LOCATION_SUBJECTS_REQUEST, LOCATION_SUBJECTS_SUCCESS, LOCATION_SUBJECTS_FAIL,
} from '../constants/locationConstants';

// Helper functions
const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/location';

export const listLocations = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_LIST_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/all`, config);
        
        // Handle both old and new response formats
        const responseData = {
            items: data.items || data,
            totalCount: data.totalCount || (data.length || 0),
            currentPage: data.page || 1,
            totalPages: data.totalPages || 1
        };
        
        dispatch({ type: LOCATION_LIST_SUCCESS, payload: responseData });
    } catch (error) {
        dispatch({
            type: LOCATION_LIST_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const listLocationsByUploadBatch = (uploadBatchId, params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_BY_UPLOAD_BATCH_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/uploadbatch/${uploadBatchId}`, config);
        
        const responseData = {
            items: data.items || data,
            totalCount: data.totalCount || (data.length || 0),
            currentPage: data.page || 1,
            totalPages: data.totalPages || 1
        };
        
        dispatch({ type: LOCATION_BY_UPLOAD_BATCH_SUCCESS, payload: responseData });
    } catch (error) {
        dispatch({
            type: LOCATION_BY_UPLOAD_BATCH_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const listLocationsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_LIST_BY_IDS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getLocationDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_DETAILS_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_DETAILS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const createLocation = (locationData) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_CREATE_REQUEST });
        const { data } = await axios.post(
            API_BASE_URL,
            locationData,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const updateLocation = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_UPDATE_REQUEST });
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const deleteLocation = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_DELETE_REQUEST });
        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_DELETE_SUCCESS });
    } catch (error) {
        dispatch({
            type: LOCATION_DELETE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const loadLocationDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_DTO_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: LOCATION_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_DTO_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

// Custom location-specific actions
export const getLocationsByOrganisation = (organisationId) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_BY_ORGANISATION_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/organisation/${organisationId}`, config);
        dispatch({ type: LOCATION_BY_ORGANISATION_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_BY_ORGANISATION_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getLocationsByStatus = (status) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_BY_STATUS_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/status/${status}`, config);
        dispatch({ type: LOCATION_BY_STATUS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_BY_STATUS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getLocationByODSCode = (odsCode) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_BY_ODS_CODE_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/odscode/${odsCode}`, config);
        dispatch({ type: LOCATION_BY_ODS_CODE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_BY_ODS_CODE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getLocationByLocationCode = (locationCode) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_BY_LOCATION_CODE_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/locationcode/${locationCode}`, config);
        dispatch({ type: LOCATION_BY_LOCATION_CODE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: LOCATION_BY_LOCATION_CODE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getLocationSubjects = (locationId, params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOCATION_SUBJECTS_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/${locationId}/subjects`, config);
        
        // Handle both old and new response formats
        const responseData = {
            items: data.items || data,
            totalCount: data.totalCount || (data.length || 0),
            currentPage: data.page || 1,
            totalPages: data.totalPages || 1,
            limit: data.limit || 10
        };
        
        dispatch({ type: LOCATION_SUBJECTS_SUCCESS, payload: responseData });
    } catch (error) {
        dispatch({
            type: LOCATION_SUBJECTS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
}; 